/** Ported from app/templates/components/visualization/moment-field-map.html
 *  (Phase 4 of the port) — the dot-rendering half. The macro's pitch SVG
 *  markup lives in the page component (FieldMap in PerformanceSummary.tsx)
 *  with the same ids; this module renders interactive moment dots into it,
 *  with the hoverable popover and click-through, exactly as the Jinja
 *  component did.
 *
 * Deliberately not ported (no caller on either page): renderHeatmap /
 * clearHeatmap, setZoneStats (the pages write zone values by element id),
 * and the debug console.logs. normalizeCoordinates was ALREADY an identity
 * function upstream — "always return raw coordinates" — so the
 * normalizeForTeam option the attacking page passes remains accepted and
 * inert, exactly as before.
 *
 * Coordinate system: centered meters (x −52.5..+52.5, y −34..+34),
 * matching the match-page canvas. CSS: legacy-css/moment-field-map.css +
 * moment-popover.css.
 */
import { ScoreRing, type ScoreThresholds } from '../../lib/viz/scoreRing'
import { buildPopoverHTML, type MomentLike } from './momentUtils'

// Field dimensions in meters (FIFA/MLS standard, matches clips canvas)
const PITCH_LENGTH = 105
const PITCH_WIDTH = 68
const HALF_LENGTH = PITCH_LENGTH / 2 // 52.5
const HALF_WIDTH = PITCH_WIDTH / 2 // 34

// SVG viewBox dimensions (10 units per meter)
const SVG_WIDTH = 1050
const SVG_HEIGHT = 680

const DOT_RADIUS = 10
const DOT_STROKE_WIDTH = 2

// Neutral dot color (matches timeline dots - avoids team color confusion)
const DOT_COLOR = 'hsl(215, 14%, 50%)' // --color-radix-slate-8

// Popover hide delay (ms) - allows mouse to move from dot to popover
const POPOVER_HIDE_DELAY = 150
let popoverHideTimeout: ReturnType<typeof setTimeout> | null = null

export type FieldMapOptions = {
  onClick?: (moment: MomentLike) => void
  /** The game's frame rate (the server's fps): the popover's time;
   *  null when the server has none. */
  fps: number | null
  normalizeForTeam?: unknown // accepted and inert, as upstream
  variant?: string // the popover ring's ScoreRing variant; the default bands when absent
  thresholds?: ScoreThresholds // the bands the 'dynamic' variant colours on
  neutral?: boolean // the popover ring's neutral (empty) state: no band to colour by
}

export function isOnField(x: number, y: number): boolean {
  return Math.abs(x) <= HALF_LENGTH && Math.abs(y) <= HALF_WIDTH
}

/** Centered meters → SVG units (y inverted for SVG). */
export function metersToSvg(x: number, y: number): { x: number; y: number } {
  const svgX = (x + HALF_LENGTH) * (SVG_WIDTH / PITCH_LENGTH)
  const svgY = (HALF_WIDTH - y) * (SVG_HEIGHT / PITCH_WIDTH)
  return { x: svgX, y: svgY }
}

function schedulePopoverHide(popoverEl: HTMLElement | null): void {
  if (popoverHideTimeout) clearTimeout(popoverHideTimeout)
  popoverHideTimeout = setTimeout(() => {
    if (popoverEl) popoverEl.classList.remove('visible')
  }, POPOVER_HIDE_DELAY)
}

function setupPopoverHover(popoverEl: HTMLElement): void {
  popoverEl.addEventListener('mouseenter', () => {
    if (popoverHideTimeout) {
      clearTimeout(popoverHideTimeout)
      popoverHideTimeout = null
    }
  })
  popoverEl.addEventListener('mouseleave', () => {
    schedulePopoverHide(popoverEl)
  })
}

/** Position the popover near the mouse, kept inside the pitch bounds. */
function positionPopover(popoverEl: HTMLElement, event: MouseEvent,
                         dotGroup: SVGGElement): void {
  const pitch = dotGroup.closest('.moment-field-map__pitch')
  if (!pitch) return

  const pitchRect = pitch.getBoundingClientRect()

  requestAnimationFrame(() => {
    const popoverRect = popoverEl.getBoundingClientRect()
    let x = event.clientX - pitchRect.left + 12
    let y = event.clientY - pitchRect.top - popoverRect.height - 8
    if (x + popoverRect.width > pitchRect.width) {
      x = event.clientX - pitchRect.left - popoverRect.width - 12
    }
    if (y < 0) {
      y = event.clientY - pitchRect.top + 16
    }
    popoverEl.style.left = `${x}px`
    popoverEl.style.top = `${y}px`
  })
}

function createSvgDot(moment: MomentLike,
                      options: FieldMapOptions & { popoverEl: HTMLElement | null }): SVGGElement {
  const { onClick, popoverEl, variant, thresholds, neutral, fps } = options
  const score = moment.score || 0
  const pos = metersToSvg(moment.x || 0, moment.y || 0)

  const ns = 'http://www.w3.org/2000/svg'
  const group = document.createElementNS(ns, 'g')
  group.setAttribute('class', 'moment-svg-dot')
  group.setAttribute('data-moment-id', String(moment.id ?? ''))
  group.setAttribute('data-frame', String(moment.frame ?? 0))
  group.style.cursor = 'pointer'

  const circle = document.createElementNS(ns, 'circle')
  circle.setAttribute('cx', String(pos.x))
  circle.setAttribute('cy', String(pos.y))
  circle.setAttribute('r', String(DOT_RADIUS))
  circle.setAttribute('fill', DOT_COLOR)
  circle.setAttribute('stroke', 'white')
  circle.setAttribute('stroke-width', String(DOT_STROKE_WIDTH))
  group.appendChild(circle)

  group.addEventListener('mouseenter', (e) => {
    if (popoverHideTimeout) {
      clearTimeout(popoverHideTimeout)
      popoverHideTimeout = null
    }

    circle.setAttribute('r', String(DOT_RADIUS * 1.3))
    circle.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'

    if (popoverEl) {
      popoverEl.innerHTML = buildPopoverHTML(moment, fps)
      popoverEl.classList.add('visible', 'use-performance-colors')
      positionPopover(popoverEl, e, group)

      // Render the score ring after the DOM update; a moment served with
      // no score, or with no band to colour it by, gets the empty ring.
      requestAnimationFrame(() => {
        if (moment.score === null || neutral) ScoreRing.renderEmpty('popover-ring', { size: 'xs' })
        else ScoreRing.render('popover-ring', score, { size: 'xs', variant, thresholds })
      })

      const playBtn = popoverEl.querySelector<HTMLButtonElement>('.moment-popover__play')
      if (playBtn && onClick) {
        playBtn.onclick = (evt) => {
          evt.stopPropagation()
          onClick(moment)
        }
      }
    }
  })

  group.addEventListener('mouseleave', () => {
    circle.setAttribute('r', String(DOT_RADIUS))
    circle.style.filter = ''
    schedulePopoverHide(popoverEl)
  })

  if (onClick) {
    group.addEventListener('click', (e) => {
      e.stopPropagation()
      circle.setAttribute('stroke', 'hsl(215, 14%, 35%)')
      circle.setAttribute('stroke-width', String(DOT_STROKE_WIDTH + 2))
      setTimeout(() => onClick(moment), 150)
    })
  }

  return group
}

const hoverWired = new WeakSet<HTMLElement>()

/** Render moments as dots on the field map with id prefix `id`. */
export function render(id: string, moments: MomentLike[],
                       options: FieldMapOptions): void {
  const dotsGroup = document.getElementById(`${id}-dots`)
  const emptyState = document.getElementById(`${id}-empty`)
  const popoverEl = document.getElementById(`${id}-popover`)

  if (!dotsGroup) return

  if (popoverEl && !hoverWired.has(popoverEl)) {
    setupPopoverHover(popoverEl)
    hoverWired.add(popoverEl)
  }

  dotsGroup.innerHTML = ''

  // Only moments on the field render; the rest are silently filtered,
  // as upstream. (normalizeCoordinates was an identity — see header.)
  const validMoments = (moments ?? []).filter((m) => isOnField(m.x ?? NaN, m.y ?? NaN))

  if (validMoments.length === 0) {
    if (emptyState) emptyState.style.display = 'flex'
    return
  }

  if (emptyState) emptyState.style.display = 'none'

  validMoments.forEach((moment) => {
    const dot = createSvgDot(moment, { ...options, popoverEl })
    dotsGroup.appendChild(dot)
  })
}

/** Update the in-field corner team labels. */
export function setLabels(id: string, leftLabel: string, rightLabel: string): void {
  const leftEl = document.getElementById(`${id}-left-label`)
  const rightEl = document.getElementById(`${id}-right-label`)
  if (leftEl) leftEl.textContent = leftLabel || ''
  if (rightEl) rightEl.textContent = rightLabel || ''
}
